import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, take, tap } from 'rxjs';
import { environment } from '@env/environment';
import { CookieService } from 'ngx-cookie';
import { StorageService } from './storage.service';
import { UserApiService } from '@http/user-api.service';
import { Router } from '@angular/router';
import { SESSION_MODE } from '@env/session-mode';
import { SessionService, UserSessionResponse } from './session.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    authChanged$ = new BehaviorSubject<boolean>(false);

    private sessionAuthenticated = false;
    private authInitialization?: Promise<boolean>;
    private logoutChannel = typeof BroadcastChannel === 'undefined'
        ? null
        : new BroadcastChannel('cd-logout-channel');

    constructor(
        private userApi: UserApiService,
        private cookies: CookieService,
        private storage: StorageService,
        private router: Router,
        private sessionService: SessionService
    ) {
        this.authChanged$.pipe(
            tap(isAuth => {
                if (isAuth) {
                    this.userApi.getAvatar().pipe(take(1)).subscribe();
                } else {
                    this.userApi.resetUserAvatar();
                }
            })
        ).subscribe();

        this.logoutChannel?.addEventListener('message', () => {
            this.clearLocalAuth();
            void this.router.navigate(['/auth']);
        });
    }

    initialize(): Promise<boolean> {
        if (SESSION_MODE === 'legacy') {
            const isAuthenticated = this.isLegacyLoggedIn;
            this.authChanged$.next(isAuthenticated);
            return Promise.resolve(isAuthenticated);
        }

        this.authInitialization ??= this.restoreSession();
        return this.authInitialization;
    }

    private async restoreSession(): Promise<boolean> {
        const legacyToken = this.accessToken;
        const legacyUser = this.userData;
        const canUpgradeLegacy = SESSION_MODE === 'dual' &&
            Boolean(legacyToken && legacyUser?.username);

        if (SESSION_MODE === 'cookie') {
            this.clearAuthCookies();
        }

        try {
            let response: UserSessionResponse;
            try {
                response = await firstValueFrom(this.sessionService.getSession());
            } catch (error) {
                if (!canUpgradeLegacy) throw error;
                response = await firstValueFrom(
                    this.sessionService.upgradeLegacySession(legacyToken, legacyUser.username)
                );
            }

            if (response.status === 'success' && !response.logged_in && canUpgradeLegacy) {
                response = await firstValueFrom(
                    this.sessionService.upgradeLegacySession(legacyToken, legacyUser.username)
                );
            }

            if (response.status === 'success' && response.logged_in) {
                this.sessionAuthenticated = true;
                this.sessionService.setCsrfToken(response.csrf_token);

                const userData = { ...(legacyUser || {}) };
                if (SESSION_MODE === 'cookie') delete userData.key;
                this.userData = {
                    ...userData,
                    id: response.user_id ?? userData.id,
                    username: response.username ?? userData.username,
                    email: response.email ?? userData.email,
                };
                this.authChanged$.next(true);
                return true;
            }
        } catch (error) {
            console.error('Failed to restore DApp session', error);
        }

        this.sessionAuthenticated = false;
        this.sessionService.clear();
        if (SESSION_MODE === 'cookie') {
            this.clearAuthCookies();
            this.storage.remove('userData');
        }

        const keepLegacySession = SESSION_MODE === 'dual' && this.isLegacyLoggedIn;
        this.authChanged$.next(keepLegacySession);
        return keepLegacySession;
    }

    authViaSignature(eth_address: string, signature: string) {
        return this.userApi.authWallet({ eth_address, signature }).pipe(
            tap(res => {
                if (res.code === 'LOGIN_OK') {
                    const hasLegacyToken = SESSION_MODE !== 'cookie' && Boolean(res.token);
                    if (hasLegacyToken) this.accessToken = res.token;

                    this.userData = {
                        username: res.username,
                        ...(hasLegacyToken ? { key: res.token } : {}),
                    };
                    this.sessionAuthenticated = SESSION_MODE !== 'legacy';
                    if (SESSION_MODE !== 'legacy') {
                        this.sessionService.setCsrfToken(res.csrf_token);
                    }
                    this.authChanged$.next(true);
                }
            })
        );
    }

    logout() {
        const finishLogout = () => {
            this.clearLocalAuth();
            this.logoutChannel?.postMessage({ redirect: true });
            void this.router.navigate(['/auth']);
        };

        if (SESSION_MODE === 'legacy') {
            this.storage.clear();
            this.cookies.removeAll();
            this.authChanged$.next(false);
            void this.router.navigate(['/auth']);
            return;
        }

        this.sessionService.logout(this.accessToken, this.userData?.username || '').pipe(take(1)).subscribe({
            next: finishLogout,
            error: finishLogout,
        });
    }

    private clearLocalAuth(): void {
        this.storage.clear();
        this.clearAuthCookies();
        this.sessionService.clear();
        this.sessionAuthenticated = false;
        this.authChanged$.next(false);
    }

    private clearAuthCookies(): void {
        this.cookies.remove('accessToken', { path: '/' });
        this.cookies.remove('tokenExpiration', { path: '/' });
    }

    get accessToken(): string {
        return SESSION_MODE === 'cookie' ? '' : (this.cookies.get('accessToken') as string);
    }

    set accessToken(val: string) {
        if (SESSION_MODE === 'cookie' || !val) {
            this.clearAuthCookies();
            return;
        }

        const expires = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000);
        const cookieOptions = {
            expires,
            path: '/',
            secure: window.location.protocol === 'https:',
            sameSite: 'strict' as const,
        };

        this.cookies.put('accessToken', val, cookieOptions);
        this.cookies.put('tokenExpiration', expires.toUTCString(), cookieOptions);
    }

    get userData(): any {
        return this.storage.get('userData') as any;
    }

    set userData(data: Partial<any>) {
        this.storage.set('userData', data);
    }

    private get isLegacyLoggedIn(): boolean {
        return Boolean(this.userData && this.accessToken);
    }

    get isLoggedIn(): boolean {
        if (SESSION_MODE === 'legacy') return this.isLegacyLoggedIn;
        if (SESSION_MODE === 'dual') return this.sessionAuthenticated || this.isLegacyLoggedIn;
        return this.sessionAuthenticated;
    }
}
